import { normalizeEqualizer, type EqualizerState } from './equalizer';
import type { MusicTrack, Playlist, RepeatMode } from './musicTypes';
import { normalizePlaylists } from './playlists';

/**
 * Опись плеера на сервере — чтобы плейлисты пережили переустановку приложения.
 *
 * <p><b>Что уходит на сервер.</b> Только опись: названия плейлистов, порядок треков в них,
 * эквалайзер и «отпечаток» каждого трека — имя файла, размер, название, исполнитель,
 * длительность. Ни звука, ни обложек: музыка по-прежнему живёт только на телефоне.
 *
 * <p><b>Как трек находит своё место.</b> Id трека не случайный, а выводится из имени файла и его
 * размера ({@link stableTrackId}). После переустановки человек добавляет те же файлы — и они
 * получают те же id, а значит, сами встают в те плейлисты и на те позиции, где были. Пока файла
 * нет, трек остаётся «призраком»: он виден в плейлисте серым, с названием и исполнителем, —
 * понятно, какой файл поискать.
 *
 * <p><b>Когда отправлять.</b> Опись сравнивается с последней отправленной по отпечатку
 * ({@link payloadHash}); отправляется, только если что-то поменялось. Поэтому подтягивание
 * описи с сервера не порождает встречной записи, а простой перезапуск приложения не пишет ничего.
 */

export const CLOUD_PAYLOAD_VERSION = 1;

/** Трек из описи: всё, что нужно, чтобы узнать файл и показать его, пока файла нет. */
export type TrackGhost = {
  id: string;
  title: string;
  artist: string;
  album: string;
  durationSec: number;
  sourceName: string;
  sizeBytes: number;
};

export type PlayerSettings = { shuffle: boolean; repeat: RepeatMode; normalizeVolume: boolean };

export type CloudPayload = {
  v: typeof CLOUD_PAYLOAD_VERSION;
  tracks: TrackGhost[];
  playlists: Playlist[];
  equalizer: EqualizerState;
  settings: PlayerSettings;
};

/**
 * Что знает клиент о своей описи на сервере.
 *
 * <p>`base` — опись, какой она была на сервере при последней встрече с ним. Без неё нельзя
 * понять, кто что поменял, когда правили оба устройства: «трека нет у меня» может значить и
 * «я его убрал», и «его добавили там». См. {@link mergeCloudPayloads}.
 */
export type CloudState = { revision: number; syncedHash: string; base?: CloudPayload | null };

export const EMPTY_CLOUD_STATE: CloudState = { revision: 0, syncedHash: '', base: null };

/** FNV-1a в двух вариантах — 64 бита на двух 32-битных: коллизий на фонотеке не будет. */
function fnv(s: string, seed: number): string {
  let h = seed >>> 0;
  for (let i = 0; i < s.length; i += 1) {
    h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  }
  return h.toString(36).padStart(7, '0');
}

/**
 * Id трека по имени и размеру исходного файла. Один и тот же файл на любом устройстве и после
 * любой переустановки получает один и тот же id — на этом держится восстановление плейлистов.
 */
export function stableTrackId(sourceName: string, sizeBytes: number): string {
  const key = `${sourceName.normalize('NFC').toLowerCase()}|${sizeBytes}`;
  return `t${fnv(key, 2166136261)}${fnv(key, 0x9747b28c)}`;
}

export function ghostOf(t: MusicTrack | TrackGhost): TrackGhost {
  return {
    id: t.id,
    title: t.title,
    artist: t.artist,
    album: t.album,
    durationSec: Math.round(t.durationSec),
    sourceName: t.sourceName,
    sizeBytes: t.sizeBytes,
  };
}

export function buildCloudPayload(state: {
  tracks: readonly MusicTrack[];
  ghosts: readonly TrackGhost[];
  playlists: readonly Playlist[];
  equalizer: EqualizerState;
  settings: PlayerSettings;
}): CloudPayload {
  const have = new Set(state.tracks.map((t) => t.id));
  return {
    v: CLOUD_PAYLOAD_VERSION,
    tracks: [...state.tracks.map(ghostOf), ...state.ghosts.filter((g) => !have.has(g.id)).map(ghostOf)],
    playlists: state.playlists.map((p) => ({ id: p.id, name: p.name, trackIds: [...p.trackIds], createdAt: p.createdAt })),
    equalizer: { enabled: state.equalizer.enabled, preset: state.equalizer.preset, gains: [...state.equalizer.gains] },
    settings: { ...state.settings },
  };
}

/** Короткий отпечаток описи: по нему решаем, есть ли что отправлять. */
export function payloadHash(p: CloudPayload): string {
  const json = JSON.stringify(p);
  return `${json.length.toString(36)}.${fnv(json, 2166136261)}${fnv(json, 0x9747b28c)}`;
}

function isGhost(x: unknown): x is TrackGhost {
  const g = x as TrackGhost;
  return Boolean(g) && typeof g.id === 'string' && typeof g.title === 'string';
}

/** Разобрать присланное сервером. Чужая или будущая версия формата — `null`, ничего не трогаем. */
export function parseCloudPayload(raw: unknown): CloudPayload | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const r = raw as Partial<CloudPayload>;
  if (r.v !== CLOUD_PAYLOAD_VERSION) {
    return null;
  }
  const tracks = (Array.isArray(r.tracks) ? r.tracks : []).filter(isGhost).map((g) => ({
    id: g.id,
    title: g.title,
    artist: typeof g.artist === 'string' ? g.artist : '',
    album: typeof g.album === 'string' ? g.album : '',
    durationSec: Number.isFinite(g.durationSec) ? g.durationSec : 0,
    sourceName: typeof g.sourceName === 'string' ? g.sourceName : '',
    sizeBytes: Number.isFinite(g.sizeBytes) ? g.sizeBytes : 0,
  }));
  const s = (r.settings ?? {}) as Partial<PlayerSettings>;
  return {
    v: CLOUD_PAYLOAD_VERSION,
    tracks,
    playlists: normalizePlaylists(r.playlists, new Set(tracks.map((t) => t.id))),
    equalizer: normalizeEqualizer(r.equalizer),
    settings: {
      shuffle: s.shuffle === true,
      repeat: s.repeat === 'off' || s.repeat === 'one' ? s.repeat : 'all',
      normalizeVolume: s.normalizeVolume !== false,
    },
  };
}

/**
 * Принять опись с сервера. Плейлисты, эквалайзер и режимы берутся оттуда; треки, которых на
 * устройстве нет, становятся призраками. Свои файлы при этом не теряются никогда: трек,
 * которого в описи нет, остаётся во «Всей музыке».
 *
 * <p>Название, исполнитель и альбом своих треков тоже берутся из описи: их могли поправить на
 * другом устройстве, а файл там и здесь один и тот же.
 */
export function applyCloudPayload(
  localTracks: readonly MusicTrack[],
  payload: CloudPayload,
): { tracks: MusicTrack[]; ghosts: TrackGhost[]; playlists: Playlist[]; equalizer: EqualizerState; settings: PlayerSettings } {
  const have = new Set(localTracks.map((t) => t.id));
  const described = new Map(payload.tracks.map((g) => [g.id, g]));
  const ghosts = payload.tracks.filter((g) => !have.has(g.id));
  const known = new Set([...have, ...ghosts.map((g) => g.id)]);
  return {
    tracks: localTracks.map((t) => {
      const g = described.get(t.id);
      if (!g || (g.title === t.title && g.artist === t.artist && g.album === t.album)) {
        return t;
      }
      return { ...t, title: g.title || t.title, artist: g.artist, album: g.album };
    }),
    ghosts,
    playlists: normalizePlaylists(payload.playlists, known),
    equalizer: payload.equalizer,
    settings: payload.settings,
  };
}

/**
 * Что делать после того, как узнали версию на сервере.
 *
 * <p>Если на сервере новее, а у нас есть своя неотправленная правка, — сливаем
 * ({@link mergeCloudPayloads}), а не выбираем одну сторону. Раньше своя правка просто
 * перезаписывала опись, и песни, добавленные в плейлист в браузере, пропадали на телефоне,
 * стоило там тем временем что-нибудь поменять.
 */
export function decideSync(
  local: { revision: number; dirty: boolean; hasContent: boolean },
  remote: { revision: number } | null,
): 'push' | 'pull' | 'merge' | 'none' {
  if (remote === null) {
    return local.dirty || local.hasContent ? 'push' : 'none';
  }
  if (remote.revision > local.revision) {
    return local.dirty && (local.hasContent || local.revision > 0) ? 'merge' : 'pull';
  }
  return local.dirty ? 'push' : 'none';
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Список id, поправленный с двух сторон: берём серверный порядок, выкидываем то, что убрали
 * у нас, и дописываем в конец то, что у нас добавили.
 */
function mergeIdList(base: readonly string[] | null, local: readonly string[], remote: readonly string[]): string[] {
  if (base && sameList(local, base)) {
    return [...remote];
  }
  if (base && sameList(remote, base)) {
    return [...local];
  }
  const was = new Set(base ?? []);
  const mine = new Set(local);
  const out = remote.filter((id) => !(was.has(id) && !mine.has(id)));
  const have = new Set(out);
  for (const id of local) {
    if (!was.has(id) && !have.has(id)) {
      out.push(id);
      have.add(id);
    }
  }
  return out;
}

/** Поле, поправленное у нас относительно базы, — наше; иначе — серверное. */
function pick<T>(base: T | undefined, local: T, remote: T, eq: (a: T, b: T) => boolean = (a, b) => a === b): T {
  return base !== undefined && !eq(local, base) ? local : remote;
}

/**
 * Слить две версии описи, поправленные независимо: здесь и на другом устройстве.
 *
 * <p>Сравниваем каждую с `base` — описью, какой её оба видели в последний раз. Добавленное с
 * любой стороны остаётся, убранное с любой стороны уходит; если одно и то же поле поменяли
 * оба, побеждает это устройство — правку на нём человек сделал только что. Без базы (первая
 * встреча с сервером) — всё объединяется, а в спорном берётся серверное.
 */
export function mergeCloudPayloads(base: CloudPayload | null, local: CloudPayload, remote: CloudPayload): CloudPayload {
  const baseTracks = new Map((base?.tracks ?? []).map((t) => [t.id, t]));
  const localTracks = new Map(local.tracks.map((t) => [t.id, t]));
  const remoteTracks = new Map(remote.tracks.map((t) => [t.id, t]));
  const keep = (id: string) => {
    const inLocal = localTracks.has(id);
    const inRemote = remoteTracks.has(id);
    // Есть с одной стороны и был в базе — значит, другая сторона его забыла.
    return (inLocal && inRemote) || !baseTracks.has(id);
  };
  const tracks: TrackGhost[] = [];
  for (const id of [...remoteTracks.keys(), ...[...localTracks.keys()].filter((k) => !remoteTracks.has(k))]) {
    if (!keep(id)) {
      continue;
    }
    const l = localTracks.get(id);
    const r = remoteTracks.get(id);
    if (!l || !r) {
      tracks.push((l ?? r) as TrackGhost);
      continue;
    }
    const b = baseTracks.get(id);
    tracks.push({
      ...r,
      title: pick(b?.title, l.title, r.title),
      artist: pick(b?.artist, l.artist, r.artist),
      album: pick(b?.album, l.album, r.album),
      durationSec: r.durationSec || l.durationSec,
    });
  }

  const basePl = new Map((base?.playlists ?? []).map((p) => [p.id, p]));
  const localPl = new Map(local.playlists.map((p) => [p.id, p]));
  const playlists: Playlist[] = [];
  for (const r of remote.playlists) {
    const l = localPl.get(r.id);
    const b = basePl.get(r.id);
    if (!l) {
      // У нас его нет: либо удалили здесь (был в базе), либо он новый оттуда.
      if (!b) {
        playlists.push(r);
      }
      continue;
    }
    playlists.push({
      ...r,
      name: pick(b?.name, l.name, r.name),
      trackIds: mergeIdList(b?.trackIds ?? null, l.trackIds, r.trackIds),
    });
  }
  const remotePlIds = new Set(remote.playlists.map((p) => p.id));
  for (const l of local.playlists) {
    if (!remotePlIds.has(l.id) && !basePl.has(l.id)) {
      playlists.push(l);
    }
  }

  const eqSame = (a: EqualizerState, b: EqualizerState) =>
    a.enabled === b.enabled && a.preset === b.preset && sameList(a.gains.map(String), b.gains.map(String));
  return {
    v: CLOUD_PAYLOAD_VERSION,
    tracks,
    playlists: normalizePlaylists(playlists, new Set(tracks.map((t) => t.id))),
    equalizer: pick(base?.equalizer, local.equalizer, remote.equalizer, eqSame),
    settings: {
      shuffle: pick(base?.settings.shuffle, local.settings.shuffle, remote.settings.shuffle),
      repeat: pick(base?.settings.repeat, local.settings.repeat, remote.settings.repeat),
      normalizeVolume: pick(base?.settings.normalizeVolume, local.settings.normalizeVolume, remote.settings.normalizeVolume),
    },
  };
}

/** Сколько треков из восстановленных вернулось на место после импорта. */
export function countRestored(added: readonly MusicTrack[], ghostsBefore: readonly TrackGhost[]): number {
  const ghostIds = new Set(ghostsBefore.map((g) => g.id));
  return added.filter((t) => ghostIds.has(t.id)).length;
}
